import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Themed replacement for the native select element (Radix Select).
 * Native selects are banned in this codebase — OS dropdown chrome
 * punches through the dark theme and cannot be keyboard-styled
 * (epic #835).
 *
 * Open state is controlled here (not left to Radix) so the dropdown can
 * close itself deterministically on any outside press. Radix's own
 * outside-dismiss is unreliable when the Select sits inside a modal
 * overlay: the press often lands on the host Dialog's overlay, which
 * Radix registers as a dismissable "branch" and therefore refuses to
 * treat as outside — the dropdown just stays open (the sibling face of
 * radix-ui/primitives#1088 / #2961 / #3461; see `nestedLayerGuard.ts`
 * for the parent-overlay face). A document-level capture listener keyed
 * only on "was the press inside the trigger or the dropdown" sidesteps
 * Radix's layer semantics entirely. It runs AFTER the host overlay's
 * dismiss-guard capture listener (guards register at overlay-open,
 * before the Select opens), so the guard still snapshots "popper open"
 * for that same press — net effect: one outside press closes the
 * dropdown and keeps the host overlay up.
 */
export interface SelectOption {
  readonly value: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
}

interface Props {
  readonly id?: string;
  readonly value: string | undefined;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  readonly ariaDescribedBy?: string;
  readonly invalid?: boolean;
  readonly required?: boolean;
  readonly mono?: boolean;
  /** 32px trigger in `text-dense` for dense rows; the default is the 36px form control. */
  readonly compact?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

export function Select({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  disabled = false,
  ariaLabel,
  ariaDescribedBy,
  invalid = false,
  required = false,
  mono = false,
  compact = false,
  className,
  testId,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    // Capture phase: fires before Radix's own (bubble-phase) outside-press
    // handling and regardless of what the press hit-tests to — overlay,
    // <html> under a body pointer-events lock, or another element.
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target) === true || contentRef.current?.contains(target) === true) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, { capture: true });
    };
  }, [open]);

  return (
    <SelectPrimitive.Root
      value={value ?? ''}
      onValueChange={onValueChange}
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
    >
      <SelectPrimitive.Trigger
        id={id}
        ref={triggerRef}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        data-testid={testId}
        className={cx(
          'inline-flex w-full items-center justify-between gap-2 rounded border border-line bg-well text-paper transition-colors duration-120 hover:border-line-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/20 data-[state=open]:border-signal-deep data-[placeholder]:text-paper-faint disabled:pointer-events-none disabled:opacity-60',
          compact ? 'h-8 px-2.5 text-dense max-md:text-field-touch' : 'h-9 px-3 text-body max-md:h-10 max-md:text-field-touch',
          invalid && 'border-bad-line',
          mono && 'font-mono',
          className,
        )}
      >
        <span className="truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-paper-low" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          ref={contentRef}
          position="popper"
          sideOffset={4}
          className="z-50 max-h-[320px] max-md:max-h-[60vh] min-w-[var(--radix-select-trigger-width)] overflow-y-auto rounded-lg border border-line bg-raised p-1 shadow-overlay animate-panel-in"
        >
          <SelectPrimitive.Viewport>
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cx(
                  'flex cursor-default select-none items-center justify-between gap-3 rounded px-2.5 py-1.5 text-dense text-paper-mid outline-none transition-colors duration-120 data-[highlighted]:bg-active data-[highlighted]:text-paper data-[disabled]:opacity-50 data-[state=checked]:text-signal max-md:py-2.5',
                  mono && 'font-mono',
                )}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator>
                  <Check aria-hidden="true" className="h-3.5 w-3.5" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
